/**
 * Pedagogy Stage — Pedagogical Contract Validation (Stage 3b)
 *
 * PEDAGOGICAL_CONTRACT_SPEC §8b — Runs between Content (Stage 3) and Visual (Stage 4).
 * Issues use domain 'pedagogy' and stage 3 (no new PipelineStage number).
 *
 * Five checks:
 * 1. DOK-tier alignment — DOK level should match tier band expectations (incl. too-high)
 * 2. Distractor misconception tagging — MC distractors should have misconception tags
 * 3. Indicator-answer parity — Visual indicator values must not leak the answer
 * 4. Structured data detection — Flag questions that rely on regex parsing
 *
 * Severity strategy (TIGHTENED):
 * - PEDAGOGY_INDICATOR_LEAKS_ANSWER: blocker (severity 80) — always
 * - PEDAGOGY_DISTRACTORS_UNTAGGED tier ≥ 9: blocker (severity 70) — even without enforce flag
 * - PEDAGOGY_REGEX_FALLBACK tier ≥ 9: blocker (severity 70) — fragile parsing is dangerous
 * - PEDAGOGY_STRUCTURED_DATA_MISSING tier ≥ 9: blocker (severity 70)
 * - DOK_TOO_LOW: coach (severity 55) → blocker (severity 75) when enforced
 * - DOK_TOO_HIGH: coach (severity 35) — informational
 * - When enforcePedagogicalContract flag is flipped, DOK/distractor become blockers for all tiers
 */

import type { Question } from '../../../content';
import type { ValidationIssue } from '../issueContract';
import { blocker, coach, info, ISSUE_CODES } from '../issueContract';
import { isPolicyFeatureEnabled, getTierBand } from '../policyEngine';
import { isKnownMisconception } from '../../data/misconceptions';
import { NON_MC_TYPES } from '../../utils';

// ─── Tier-band → minimum DOK expectations ─────────────────────────────────────

const MIN_DOK_BY_TIER_BAND: Record<string, number> = {
  basic: 1,
  // Tiers 1-4: recall is fine
  intermediate: 1,
  // Tiers 5-8: recall still acceptable
  advanced: 2,
  // Tiers 9-12: should require at least application
  expert: 3 // Tiers 13-16: must be strategic thinking or higher
};
const MAX_DOK_BY_TIER_BAND: Record<string, number> = {
  basic: 2,
  // Tiers 1-4: no strategic thinking expected
  intermediate: 3,
  // Tiers 5-8: up to strategic thinking
  advanced: 4,
  // Tiers 9-12: all levels
  expert: 4 // Tiers 13-16: all levels
};

// ─── Helper: detect regex-parsed operands (ColumnArithmetic/LongDivision pattern) ─

const ARITHMETIC_REGEX_PATTERNS = [/(\d+)\s*[+\-×÷]\s*(\d+)/,
// "123 + 456" in question text
/(\d+)\s*:\s*(\d+)/,
// "144 : 12" division notation
/Bereken\s+(\d+)\s*[+\-×÷:]/i // "Bereken 123 +" pattern
];
function detectsRegexFallback(question: Question): boolean {
  // Only flag arithmetic types that parse from question text
  const regexTypes: readonly string[] = ['cijferend_rekenen', 'cijferend_delen'];
  if (!regexTypes.includes(question.questionType)) return false;

  // If structured data exists with operands, no fallback needed
  const data = question.data;
  if (data) {
    // New data-first format (PEDAGOGICAL_CONTRACT_SPEC §7)
    if (data.columnArithmetic && typeof (data.columnArithmetic as any).operand1 === 'number') return false;
    if (data.longDivision && typeof (data.longDivision as any).dividend === 'number') return false;
    // Legacy flat format
    if (data.operand1 !== undefined || data.operands !== undefined) return false;
  }

  // Check if question text contains parseable arithmetic
  return ARITHMETIC_REGEX_PATTERNS.some(re => re.test(question.question));
}

// ─── Check 1: DOK-tier alignment ──────────────────────────────────────────────

function checkDokAlignment(question: Question, enforce: boolean): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const dok = question.pedagogy?.dok;
  if (dok === undefined) {
    // DOK not set — blocker for tier ≥ 5 when enforced, coach otherwise
    if (question.tier >= 5 || enforce) {
      const issue = enforce ? blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DOK_MISSING, `Vraag ${question.id} heeft geen DOK-niveau ingesteld.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Voeg een pedagogy.dok waarde (1-4) toe aan de seed data.',
        severity: 75
      }) : coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DOK_MISSING, `Vraag ${question.id} heeft geen DOK-niveau ingesteld.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Voeg een pedagogy.dok waarde (1-4) toe aan de seed data.',
        severity: 50
      });
      issues.push(issue);
    }
    return issues;
  }

  // Check DOK vs tier band
  const tierBand = getTierBand(question.tier);
  const minDok = MIN_DOK_BY_TIER_BAND[tierBand] ?? 1;
  const maxDok = MAX_DOK_BY_TIER_BAND[tierBand] ?? 4;
  if (dok < minDok) {
    const issue = enforce ? blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DOK_TOO_LOW, `Vraag ${question.id} heeft DOK ${dok}, maar tier-band '${tierBand}' vereist minimaal DOK ${minDok}.`, {
      questionId: question.id,
      familyId: question.familyId,
      expected: `DOK ≥ ${minDok}`,
      actual: `DOK ${dok}`,
      severity: 75
    }) : coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DOK_TOO_LOW, `Vraag ${question.id} heeft DOK ${dok}, maar tier-band '${tierBand}' verwacht minimaal DOK ${minDok}.`, {
      questionId: question.id,
      familyId: question.familyId,
      expected: `DOK ≥ ${minDok}`,
      actual: `DOK ${dok}`,
      severity: 55
    });
    issues.push(issue);
  }
  if (dok > maxDok) {
    issues.push(coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DOK_TOO_HIGH, `Vraag ${question.id} heeft DOK ${dok}, onverwacht hoog voor tier-band '${tierBand}' (maximum DOK ${maxDok}).`, {
      questionId: question.id,
      familyId: question.familyId,
      expected: `DOK ≤ ${maxDok}`,
      actual: `DOK ${dok}`,
      severity: 35
    }));
  }
  return issues;
}

// ─── Check 2: Distractor misconception tagging ───────────────────────────────

function checkDistractorTags(question: Question, enforce: boolean): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Only applies to MC questions with options
  if (NON_MC_TYPES.includes(question.questionType)) return issues;
  if (!question.options || question.options.length < 2) return issues;
  const tags = question.pedagogy?.misconceptionTags;
  const distractorCount = question.options.length - 1; // exclude correct answer

  if (!tags || tags.length === 0) {
    // No misconception tags at all
    // Blocker when enforced, or for tier ≥ 9 (high-stakes content must have tagged distractors)
    const shouldBlock = enforce || question.tier >= 9;
    const issue = shouldBlock ? blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DISTRACTORS_UNTAGGED, `Vraag ${question.id} heeft ${distractorCount} afleiders zonder misconceptie-tags.`, {
      questionId: question.id,
      familyId: question.familyId,
      suggestion: 'Voeg pedagogy.misconceptionTags toe voor elke afleider.',
      severity: 70
    }) : coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DISTRACTORS_UNTAGGED, `Vraag ${question.id} heeft ${distractorCount} afleiders zonder misconceptie-tags.`, {
      questionId: question.id,
      familyId: question.familyId,
      suggestion: 'Voeg pedagogy.misconceptionTags toe voor elke afleider.',
      severity: question.tier >= 7 ? 55 : 40
    });
    issues.push(issue);
  } else if (tags.length < distractorCount) {
    // Some distractors tagged, but not all — coach for tier ≥ 7, info otherwise
    const isHighTier = question.tier >= 7;
    issues.push(isHighTier ? coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DISTRACTORS_PARTIAL, `Vraag ${question.id} heeft ${tags.length}/${distractorCount} afleiders getagd met misconcepties.`, {
      questionId: question.id,
      familyId: question.familyId,
      suggestion: 'Tag alle afleiders met een misconceptie voor volledige didactische dekking.',
      severity: 45
    }) : info('pedagogy', 3, ISSUE_CODES.PEDAGOGY_DISTRACTORS_PARTIAL, `Vraag ${question.id} heeft ${tags.length}/${distractorCount} afleiders getagd met misconcepties.`, {
      questionId: question.id,
      familyId: question.familyId,
      suggestion: 'Tag alle afleiders met een misconceptie voor volledige didactische dekking.',
      severity: 25
    }));
  }

  // Validate misconception tag IDs against the central registry
  if (tags && tags.length > 0) {
    const unknownTags = tags.filter(t => !isKnownMisconception(t.id));
    if (unknownTags.length > 0) {
      issues.push(info('pedagogy', 3, ISSUE_CODES.PEDAGOGY_MISCONCEPTION_UNKNOWN, `Vraag ${question.id} heeft ${unknownTags.length} onbekende misconceptie-tag(s): ${unknownTags.map(t => t.id).join(', ')}. Voeg toe aan misconceptions registry of corrigeer de ID.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Registreer nieuwe misconceptie-IDs in governance/data/misconceptions.ts of gebruik een bestaande ID.',
        severity: 30
      }));
    }
  }
  return issues;
}

// ─── Check 3: Indicator-answer parity (BLOCKER from day 1) ───────────────────

function checkIndicatorParity(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];

  // Instruction mode explicitly allows visuals to show the answer
  const mode = question.pedagogy?.mode ?? 'practice';
  if (mode === 'instruction') return issues;

  // Configurable tolerance (default 5%, spec §4)
  const tolerance = question.pedagogy?.indicatorParityTolerance ?? 0.05;

  // Check stimulus aids for indicator values that leak the answer
  const allAids = [...(question.stimulusAids ?? []), ...(question.supportAids ?? [])];
  for (const aid of allAids) {
    if (aid.indicatorValue === undefined) continue;
    const indicatorStr = String(aid.indicatorValue).trim().toLowerCase();
    const correctIdx = question.correctAnswer;
    const correctOption = question.options?.[correctIdx];
    if (!correctOption) continue;
    const correctStr = correctOption.trim().toLowerCase();

    // Direct string match
    if (indicatorStr === correctStr) {
      issues.push(blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_INDICATOR_LEAKS_ANSWER, `Visueel hulpmiddel (${aid.type}) toont indicatorwaarde "${aid.indicatorValue}" die exact overeenkomt met het correcte antwoord.`, {
        questionId: question.id,
        familyId: question.familyId,
        field: `stimulusAids[${aid.type}].indicatorValue`,
        expected: 'Indicator mag het antwoord niet onthullen',
        actual: `indicatorValue="${aid.indicatorValue}" === correctAnswer="${correctOption}"`,
        severity: 80
      }));
      continue;
    }

    // Numeric proximity check (within configurable tolerance)
    const indicatorNum = parseFloat(indicatorStr);
    const correctNum = parseFloat(correctStr);
    if (!isNaN(indicatorNum) && !isNaN(correctNum) && correctNum !== 0) {
      const range = Math.abs(correctNum) || 1;
      const relDiff = Math.abs(indicatorNum - correctNum) / range;
      if (relDiff < tolerance) {
        issues.push(blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_INDICATOR_LEAKS_ANSWER, `Visueel hulpmiddel (${aid.type}) toont indicatorwaarde ${aid.indicatorValue} die binnen ${(tolerance * 100).toFixed(0)}% van het correcte antwoord ${correctOption} ligt.`, {
          questionId: question.id,
          familyId: question.familyId,
          field: `stimulusAids[${aid.type}].indicatorValue`,
          expected: `Indicator moet >${(tolerance * 100).toFixed(0)}% afwijken van het antwoord`,
          actual: `indicatorValue=${aid.indicatorValue}, correctAnswer=${correctOption}, verschil=${(relDiff * 100).toFixed(2)}%`,
          severity: 80
        }));
      }
    }
  }
  return issues;
}

// ─── Check 4: Structured data detection (regex fallback) ─────────────────────

function checkStructuredData(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = [];
  const tier = question.tier ?? 1;

  // Check explicit flag first
  if (question.pedagogy?.hasStructuredData === false) {
    if (tier >= 9) {
      issues.push(blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_STRUCTURED_DATA_MISSING, `Vraag ${question.id} (tier ${tier}) is gemarkeerd als zonder gestructureerde data — renderer moet operanden uit vraagtekst parsen. Blocker voor tier ≥ 9.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Migreer naar data.columnArithmetic of data.longDivision (zie PEDAGOGICAL_CONTRACT_SPEC §7).',
        severity: 70
      }));
    } else {
      issues.push(coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_STRUCTURED_DATA_MISSING, `Vraag ${question.id} is gemarkeerd als zonder gestructureerde data — renderer moet operanden uit vraagtekst parsen.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Migreer naar data.columnArithmetic of data.longDivision (zie PEDAGOGICAL_CONTRACT_SPEC §7).',
        severity: 50
      }));
    }
    return issues;
  }

  // Auto-detect regex fallback pattern
  if (detectsRegexFallback(question)) {
    if (tier >= 9) {
      issues.push(blocker('pedagogy', 3, ISSUE_CODES.PEDAGOGY_REGEX_FALLBACK, `Vraag ${question.id} (tier ${tier}, ${question.questionType}) parst operanden uit vraagtekst. Blocker voor tier ≥ 9 — fragiele parsing kan foutieve antwoorden veroorzaken.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Voeg data.columnArithmetic of data.longDivision toe aan de seed (zie PEDAGOGICAL_CONTRACT_SPEC §7).',
        severity: 70
      }));
    } else {
      issues.push(coach('pedagogy', 3, ISSUE_CODES.PEDAGOGY_REGEX_FALLBACK, `Vraag ${question.id} (${question.questionType}) lijkt operanden uit vraagtekst te parsen i.p.v. gestructureerde data te gebruiken.`, {
        questionId: question.id,
        familyId: question.familyId,
        suggestion: 'Voeg data.columnArithmetic of data.longDivision toe aan de seed (zie PEDAGOGICAL_CONTRACT_SPEC §7).',
        severity: 50
      }));
    }
  }
  return issues;
}

// ─── Stage Entry Point ────────────────────────────────────────────────────────

/**
 * Run the pedagogy stage on a single question.
 *
 * Issues use domain 'pedagogy' and stage 3 (runs as Stage 3b).
 * The enforcePedagogicalContract feature flag controls whether
 * DOK/distractor issues are blockers or coaches.
 */
export function runPedagogyStage(question: Question): ValidationIssue[] {
  const enforce = isPolicyFeatureEnabled('enforcePedagogicalContract');
  const issues: ValidationIssue[] = [];

  // Check 1: DOK-tier alignment
  issues.push(...checkDokAlignment(question, enforce));

  // Check 2: Distractor misconception tagging
  issues.push(...checkDistractorTags(question, enforce));

  // Check 3: Indicator-answer parity (always blocker)
  issues.push(...checkIndicatorParity(question));

  // Check 4: Structured data detection
  issues.push(...checkStructuredData(question));
  return issues;
}